import set from "lodash/set";
function adminToFirestoreIdentity() {
    return (modelObject) => {
        return modelObject;
    };
}
function clientToFirestoreIdentity() {
    return (modelObject) => {
        return modelObject;
    };
}
/**
 * Wrap an existing admin converter into one that also sets a specified field path to the document's collection key/id
 *
 * Note that, during fromFirestore, the converter sees the unmodified snapshot from Firestore (and can do its own processing
 * with snapshot.id if it wants to), and the idField isn't set until the converter has returned the model object. This
 * means the converter being wrapped shouldn't validate the idField is set if it may not be in historical data.
 *
 * @param converter An admin DataConverter impl for type T
 * @param idField A property name of T that is overwritten with the document's ID when loading from Firestore,
 *                overwriting any existing value on the model type T
 * @returns A safe converter that knows how to convert to and from the Firestore format and T, and always returns values
 *          with idField set
 */
export function wrapAdminConverterWithIdField(converter, idField) {
    return {
        toFirestore: (modelObject, options) => {
            return options
                ? converter.toFirestore(modelObject, options)
                : converter.toFirestore(modelObject);
        },
        fromFirestore: (snapshot) => {
            const model = converter.fromFirestore(snapshot);
            if (model && typeof model === "object") {
                set(model, idField, snapshot.id);
            }
            return model;
        },
    };
}
/**
 * Wrap an existing client converter into one that also sets a specified field path to the document's collection key/id
 *
 * Note that, during fromFirestore, the converter sees the unmodified snapshot from Firestore (and can do its own processing
 * with snapshot.id if it wants to), and the idField isn't set until the converter has returned the model object. This
 * means the converter being wrapped shouldn't validate the idField is set if it may not be in historical data.
 *
 * @param converter A client DataConverter impl for type T
 * @param idField A property name of T that is overwritten with the document's ID when loading from Firestore,
 *                overwriting any existing value on the model type T
 * @returns A safe converter that knows how to convert to and from the Firestore format and T, and always returns values
 *          with idField set
 */
export function wrapClientConverterWithIdField(converter, idField) {
    return {
        toFirestore: (modelObject, options) => {
            return options ? converter.toFirestore(modelObject, options) : converter.toFirestore(modelObject);
        },
        fromFirestore: (snapshot, options) => {
            const model = converter.fromFirestore(snapshot, options);
            if (model && typeof model === "object") {
                set(model, idField, snapshot.id);
            }
            return model;
        },
    };
}
/**
 * Create an admin Firestore converter from a zod schema
 *
 * @param schema A zod schema for type T
 * @param toFirestore An optional toFirestore function to convert T to a Firestore document data object. Defaults to the identity function.
 * @returns A safe converter that knows how to convert to and from the Firestore format and T, and that throws errors
 *          if we try to load malformed data from Firestore.
 */
export function adminConverterFromZod(schema, toFirestore = adminToFirestoreIdentity()) {
    return {
        toFirestore,
        fromFirestore: (snapshot) => {
            return schema.parse(snapshot.data());
        },
    };
}
/**
 * Create a client Firestore converter from a zod schema
 *
 * @param schema A zod schema for type T
 * @param toFirestore An optional toFirestore function to convert T to a Firestore document data object. Defaults to the identity function.
 * @returns A safe converter that knows how to convert to and from the Firestore format and T, and that throws errors
 *          if we try to load malformed data from Firestore.
 */
export function clientConverterFromZod(schema, toFirestore = clientToFirestoreIdentity()) {
    return {
        toFirestore,
        fromFirestore: (snapshot) => {
            return schema.parse(snapshot.data());
        },
    };
}
/**
 * Create an admin Firestore converter from a zod schema which also sets a specified field path to the document's collection key/id
 *
 * @param schema A zod schema for type T
 * @param idField A property name of T that is overwritten with the document's ID when loading from Firestore,
 *                overwriting any existing value on the model type T
 * @param toFirestore An optional toFirestore function to convert T to a Firestore document data object. Defaults to the identity function.
 * @returns A safe converter that knows how to convert to and from the Firestore format and T, and always returns values
 *          with idField set, and that throws errors if we try to load malformed data from Firestore.
 */
export function adminConverterFromZodWithIdField(schema, idField, toFirestore = adminToFirestoreIdentity()) {
    return {
        toFirestore,
        fromFirestore: (snapshot) => {
            const data = snapshot.data();
            set(data, idField, snapshot.id);
            return schema.parse(data);
        },
    };
}
/**
 * Create a client Firestore converter from a zod schema which also sets a specified field path to the document's collection key/id
 *
 * @param schema A zod schema for type T
 * @param idField A property name of T that is overwritten with the document's ID when loading from Firestore,
 *                overwriting any existing value on the model type T
 * @param toFirestore An optional toFirestore function to convert T to a Firestore document data object. Defaults to the identity function.
 * @returns A safe converter that knows how to convert to and from the Firestore format and T, and always returns values
 *          with idField set, and that throws errors if we try to load malformed data from Firestore.
 */
export function clientConverterFromZodWithIdField(schema, idField, toFirestore = clientToFirestoreIdentity()) {
    return {
        toFirestore,
        fromFirestore: (snapshot) => {
            const data = snapshot.data();
            set(data, idField, snapshot.id);
            return schema.parse(data);
        },
    };
}
//# sourceMappingURL=converter.js.map